/**
 * Admin API Request Module
 * 代理到 http://localhost:8999/addons/dramas/
 */

import type {
  ApiResponse,
  LoginResponse,
  UserInfo,
  WalletLogResponse,
  RechargeOrderListResponse,
  AppInfo,
  AppListResponse,
  AppRights,
  PayOrderInfo,
  PayOrderListResponse,
  PayReportOverview,
  PayReportTrend,
  PayReportChannels,
  PayReportQuery,
  PayReportGranularity,
  PayHistoryListResponse,
  PayHistoryDetail,
  PayHistoryQuery,
} from '../types/api';

const BASE_URL = '/addons/dramas';

// 通用请求方法
async function request<T>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('admin_token');

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { token: token } : {}),
  };

  const response = await fetch(`${BASE_URL}${url}`, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  const data = await response.json();
  return data as ApiResponse<T>;
}

// GET 请求
async function get<T>(url: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
  const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<T>(`${url}${queryString}`, { method: 'GET' });
}

// POST 请求
async function post<T>(url: string, body?: Record<string, unknown>): Promise<ApiResponse<T>> {
  return request<T>(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

// ==================== 登录相关 API ====================

export const authApi = {
  // 账号密码登录
  accountLogin: (account: string, password: string) => post<LoginResponse>('/user/accountLogin', { account, password }),

  // 验证码登录/注册
  captchaLogin: (mobile: string, code: string) => post<LoginResponse>('/user/captchaLogin', { mobile, code }),

  // 注册
  register: (data: { account: string; captcha: string; password: string; platform: string }) =>
    post<LoginResponse>('/user/captchaLogin', data),

  // 发送验证码
  sendSms: (account: string, event: string = 'register') => post('/sms/send', { account, event }),

  // 找回密码
  resetPassword: (mobile: string, code: string, newpassword: string) =>
    post('/user/resetpwd', { mobile, code, newpassword }),

  // 游客登录
  guestLogin: () => post<LoginResponse>('/user/guestLogin'),

  // 获取用户信息
  getUserInfo: () => get<UserInfo>('/user/index'),

  // 退出登录
  logout: () => post('/user/logout'),
};

// ==================== 用户相关 API ====================

export const userApi = {
  // 获取用户信息
  getInfo: () => get<UserInfo>('/user/index'),

  // 获取用户数据
  getUserData: () => get('/user/userData'),

  // 修改密码
  changePassword: (oldpassword: string, newpassword: string) => post('/user/changePwd', { oldpassword, newpassword }),

  // 修改手机号
  changeMobile: (mobile: string, code: string) => post('/user/changemobile', { mobile, code }),

  // 修改邮箱
  changeEmail: (email: string, code: string) => post('/user/changeemail', { email, code }),

  // 更新用户资料
  updateProfile: (data: { nickname?: string; avatar?: string }) => post('/user/profile', data),
};

// ==================== 钱包/交易相关 API ====================

export const walletApi = {
  // 获取交易记录 (剧场积分记录)
  getLogs: (_page?: number, status?: string, date?: string) =>
    get<WalletLogResponse>('/usable/order_list', {
      wallet_type: 'usable',
      status: status || 'all',
      ...(date ? { date } : {}),
    }),

  // 获取充值订单列表 (分页)
  getOrders: (page: number = 1, status?: string) =>
    get<RechargeOrderListResponse>('/usable/order_list', {
      page: String(page),
      ...(status && status !== 'all' ? { status } : {}),
    }),

  // 获取积分套餐列表
  getPackages: () => get('/usable/index'),

  // 获取充值订单列表
  getRechargeOrders: () => get('/usable/order_list'),

  // 创建充值订单 (剧场积分套餐)
  createRechargeOrder: (usable_id: number, total_fee: string, platform: string = 'H5') =>
    post<{ order_sn: string }>('/usable/recharge', {
      usable_id,
      total_fee,
      platform,
    }),

  // 创建自定义金额充值订单
  createCustomOrder: (amount: number, platform: string = 'H5') =>
    post<{ order_sn: string }>('/usable/rechargeMoney', {
      usable_id: 0,
      total_fee: String(amount),
      platform,
    }),
};

// ==================== 支付相关 API ====================

export const payApi = {
  // PayPal 支付
  paypal: (order_sn: string, platform: string = 'H5', cancel_url?: string, return_url?: string) =>
    post<{ pay_data: Record<string, unknown> }>('/pay/paypal', {
      order_sn,
      payment: 'paypal',
      platform,
      cancel_url: cancel_url || window.location.origin + '/admin/top-up?cancel=1',
      return_url: return_url || window.location.origin + '/admin/dashboard?success=1',
    }),

  // Stripe 支付
  stripe: (order_sn: string, platform: string = 'H5', cancel_url?: string, return_url?: string) =>
    post<{ pay_data: Record<string, unknown> }>('/pay/paypal', {
      order_sn,
      payment: 'stripe',
      platform,
      cancel_url: cancel_url || window.location.origin + '/admin/top-up?cancel=1',
      return_url: return_url || window.location.origin + '/admin/dashboard?success=1',
    }),

  // 检查支付状态
  checkPay: (order_sn: string) => post('/pay/checkPay', { order_sn }),
};

// ==================== 代理商相关 API ====================

export const resellerApi = {
  // 获取代理商信息
  getInfo: () => get('/reseller/info'),

  // 获取推广统计
  getStats: () => get('/reseller/stats'),

  // 获取下级用户列表
  getSubUsers: (page: number = 1, limit: number = 20) =>
    get('/reseller/user', { page: String(page), limit: String(limit) }),

  // 获取佣金记录
  getCommissionLogs: (page: number = 1, limit: number = 20) =>
    get('/reseller/log', { page: String(page), limit: String(limit) }),

  // 获取充值订单
  getRechargeOrders: (page: number = 1) => get('/reseller/rechargeOrder', { page: String(page) }),

  // 申请成为代理商
  apply: () => post('/reseller/apply'),
};

// ==================== 应用接入相关 API ====================

export const appApi = {
  // 应用列表
  getList: (page: number = 1, pageSize: number = 10) =>
    get<AppListResponse>('/app/index', { page: String(page), page_size: String(pageSize) }),

  // 当前账号支付应用权限
  getRights: () => get<AppRights>('/app/rights'),

  // 创建应用
  create: (name: string, notify_url: string) => post<AppInfo>('/app/create', { name, notify_url }),

  // 修改支付通知地址
  updateNotifyUrl: (id: number, notify_url: string) =>
    post<AppInfo>('/app/updateNotifyUrl', { id, notify_url }),

  // 删除应用
  delete: (id: number) => post('/app/delete', { id }),
};

// ==================== 支付订单相关 API ====================

export const payOrderApi = {
  // 支付订单列表
  getList: (page: number = 1, status?: string, order_sn?: string) =>
    get<PayOrderListResponse>('/payorder/index', {
      page: String(page),
      ...(status && status !== 'all' ? { status } : {}),
      ...(order_sn ? { order_sn } : {}),
    }),

  // 支付订单详情
  getDetail: (id: number) => get<PayOrderInfo>('/payorder/detail', { id: String(id) }),
};

// ==================== 支付报表 / 支付记录 API ====================

/** 把过滤条件转换成只含非空值的查询参数 */
function reportParams(query: PayReportQuery = {}): Record<string, string> {
  const params: Record<string, string> = {};
  if (query.app_id !== undefined && query.app_id !== '' && query.app_id !== 0 && query.app_id !== '0') {
    params.app_id = String(query.app_id);
  }
  if (query.start_time) params.start_time = query.start_time;
  if (query.end_time) params.end_time = query.end_time;
  if (query.currency) params.currency = query.currency;
  return params;
}

export const payReportApi = {
  // 统计总览（按币种）
  getOverview: (query?: PayReportQuery) =>
    get<PayReportOverview>('/payreport/overview', reportParams(query)),

  // 时间趋势（已按时间桶补零）
  getTrend: (query?: PayReportQuery, gran: PayReportGranularity = 'day') =>
    get<PayReportTrend>('/payreport/trend', { ...reportParams(query), gran }),

  // 渠道分布
  getChannels: (query?: PayReportQuery) =>
    get<PayReportChannels>('/payreport/channels', reportParams(query)),

  // 支付历史列表
  getHistory: (query?: PayHistoryQuery) => {
    const params = reportParams(query);
    if (query?.status !== undefined && query.status !== '' && query.status !== 'all') {
      params.status = String(query.status);
    }
    if (query?.channel) params.channel = query.channel;
    if (query?.order_sn) params.order_sn = query.order_sn;
    if (query?.out_trade_no) params.out_trade_no = query.out_trade_no;
    if (query?.transaction_id) params.transaction_id = query.transaction_id;
    params.page = String(query?.page || 1);
    params.page_size = String(query?.page_size || 20);
    return get<PayHistoryListResponse>('/payreport/history', params);
  },

  // 支付历史详情
  getHistoryDetail: (id: number) =>
    get<PayHistoryDetail>('/payreport/historyDetail', { id: String(id) }),
};

export default {
  auth: authApi,
  user: userApi,
  wallet: walletApi,
  pay: payApi,
  reseller: resellerApi,
  app: appApi,
  payOrder: payOrderApi,
  payReport: payReportApi,
};
